import React, { useState, useEffect } from 'react';
import { Tabs, Radio, Button, Avatar, List, Spin, message } from 'antd';
import { UserOutlined, ApartmentOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import {
  PermissionApi,
  type PermissionMode,
  type GranteeVO,
} from '../../api/permissionApi';
import GranteePickerModal from './GranteePickerModal';

interface OntologyPermissionPanelProps {
  ontologyId: number;
}

const MODE_OPTIONS = [
  { label: '仅自己', value: 'PRIVATE' as PermissionMode },
  { label: '所有人', value: 'PUBLIC' as PermissionMode },
  { label: '指定范围', value: 'CUSTOM' as PermissionMode },
];

const OntologyPermissionPanel: React.FC<OntologyPermissionPanelProps> = ({ ontologyId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<PermissionMode>('PRIVATE');
  const [editMode, setEditMode] = useState<PermissionMode>('PRIVATE');
  const [viewGrantees, setViewGrantees] = useState<GranteeVO[]>([]);
  const [editGrantees, setEditGrantees] = useState<GranteeVO[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    loadPermissions();
  }, [ontologyId]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const data = await PermissionApi.getPermissions(ontologyId);
      setViewMode(data.viewMode ?? 'PRIVATE');
      setEditMode(data.editMode ?? 'PRIVATE');
      setViewGrantees(data.viewGrantees ?? []);
      setEditGrantees(data.editGrantees ?? []);
    } catch {
      message.error('加载权限配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await PermissionApi.savePermissions(ontologyId, {
        viewMode,
        editMode,
        viewGrantees: viewGrantees.map(g => ({ granteeType: g.granteeType, granteeId: g.granteeId })),
        editGrantees: editGrantees.map(g => ({ granteeType: g.granteeType, granteeId: g.granteeId })),
      });
      message.success('权限配置已保存');
    } catch {
      message.error('保存权限配置失败');
    } finally {
      setSaving(false);
    }
  };

  const openPicker = (target: 'view' | 'edit') => {
    setPickerTarget(target);
    setPickerOpen(true);
  };

  const handlePickerConfirm = (selected: GranteeVO[]) => {
    if (pickerTarget === 'view') {
      setViewGrantees(selected);
    } else {
      setEditGrantees(selected);
    }
    setPickerOpen(false);
  };

  const removeGrantee = (target: 'view' | 'edit', granteeType: GranteeVO['granteeType'], granteeId: number) => {
    const filter = (list: GranteeVO[]) =>
      list.filter(g => !(g.granteeType === granteeType && g.granteeId === granteeId));
    if (target === 'view') setViewGrantees(prev => filter(prev));
    else setEditGrantees(prev => filter(prev));
  };

  const renderGranteeList = (target: 'view' | 'edit', grantees: GranteeVO[]) => (
    <div className="mt-3 space-y-2">
      {grantees.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {grantees.map(item => (
            <div
              key={`${item.granteeType}-${item.granteeId}`}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full border bg-gray-50 hover:bg-gray-100 transition-colors group"
              style={{ maxWidth: 200 }}
            >
              {item.granteeType === 'USER' ? (
                <Avatar size={18} icon={<UserOutlined />} src={item.avatarUrl ?? undefined} style={{ background: '#1677ff', flexShrink: 0 }} />
              ) : (
                <Avatar size={18} icon={<ApartmentOutlined />} style={{ background: '#52c41a', flexShrink: 0 }} />
              )}
              <span className="text-xs truncate max-w-[100px]">{item.displayName}</span>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ fontSize: 10 }} />}
                className="!w-4 !h-4 !min-w-0 !p-0 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity"
                onClick={() => removeGrantee(target, item.granteeType, item.granteeId)}
              />
            </div>
          ))}
        </div>
      )}
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() => openPicker(target)}
      >
        添加部门/人员
      </Button>
    </div>
  );

  const renderTabContent = (target: 'view' | 'edit') => {
    const mode = target === 'view' ? viewMode : editMode;
    const setMode = target === 'view' ? setViewMode : setEditMode;
    const grantees = target === 'view' ? viewGrantees : editGrantees;

    return (
      <div className="space-y-3 pt-2">
        <Radio.Group
          value={mode}
          onChange={e => setMode(e.target.value)}
          options={MODE_OPTIONS}
          optionType="button"
          buttonStyle="outline"
        />
        {mode === 'CUSTOM' && renderGranteeList(target, grantees)}
      </div>
    );
  };

  if (loading) {
    return <Spin className="flex justify-center mt-8" />;
  }

  return (
    <div className="space-y-4">
      <Tabs
        items={[
          { key: 'edit', label: '编辑权限', children: renderTabContent('edit') },
          { key: 'view', label: '查看权限', children: renderTabContent('view') },
        ]}
      />

      <div className="flex justify-end">
        <Button type="primary" loading={saving} onClick={handleSave}>
          保存
        </Button>
      </div>

      <GranteePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        initialSelected={pickerTarget === 'view' ? viewGrantees : editGrantees}
      />
    </div>
  );
};

export default OntologyPermissionPanel;
